/**
 * Suppliers Module — React Query Hooks
 */

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '@/core';
import {
  getAllSuppliers,
  getSupplierById,
  createSupplier,
  updateSupplier,
  deleteSupplier,
  getSuppliersPaginated,
} from '../services/supplierService';
import { SupplierFormData, SupplierListFilters, PaginatedSupplierResponse } from '../types';
import { notify } from '@/shared/services/notificationService';

const QUERY_KEY = 'suppliers';

/** Infinite scroll hook for supplier list */
export function useInfiniteSuppliers(filters?: SupplierListFilters, pageSize = 20) {
  const q = useInfiniteQuery<PaginatedSupplierResponse>({
    queryKey: [QUERY_KEY, 'infinite', filters],
    queryFn: ({ pageParam }) => getSuppliersPaginated(filters, pageParam as string | undefined, pageSize),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

  return {
    suppliers: q.data?.pages.flatMap(p => p.data) ?? [],
    totalCount: q.data?.pages[0]?.totalCount ?? 0,
    isLoading: q.isLoading,
    isFetchingNextPage: q.isFetchingNextPage,
    hasNextPage: q.hasNextPage,
    fetchNextPage: q.fetchNextPage,
    refetch: q.refetch,
  };
}

/** Single supplier by ID */
export function useSupplier(id?: string) {
  return useQuery({
    queryKey: [QUERY_KEY, id],
    queryFn: () => getSupplierById(id!),
    enabled: !!id,
  });
}

/** Create supplier mutation */
export function useCreateSupplier() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ data, locationId }: { data: SupplierFormData; locationId: string }) =>
      createSupplier(data, locationId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [QUERY_KEY] });
      notify.success(t('suppliers.messages.created', 'Supplier created'));
    },
    onError: () => notify.error(t('suppliers.messages.createError', 'Failed to create supplier')),
  });
}

/** Update supplier mutation */
export function useUpdateSupplier() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<SupplierFormData> }) =>
      updateSupplier(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [QUERY_KEY] });
      notify.success(t('suppliers.messages.updated', 'Supplier updated'));
    },
    onError: () => notify.error(t('suppliers.messages.updateError', 'Failed to update supplier')),
  });
}

/** Delete supplier mutation */
export function useDeleteSupplier() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteSupplier(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [QUERY_KEY] });
      notify.success(t('suppliers.messages.deleted', 'Supplier deleted'));
    },
    onError: () => notify.error(t('suppliers.messages.deleteError', 'Failed to delete supplier')),
  });
}
